import { fromJS } from 'immutable';

import {
  type ApplicationBackgroundUpdateAction,
  type SearchUIStateUpdateAction,
  type SetCurrentScreenDimensionsAction,
  type SetSmallScreenUiAction,
  type UpdateDominantColorAction,
} from 'src/types';

import * as Constants from '../constants';
import { AppActions, AppBgKeys, BgThemes } from '../constants/App';
import ViewActions from '../constants/ViewActions';
import { Map } from '../lib/ImmutableUtils';
import { createReducer } from '../lib/ReducerUtils';
import createFilter from '../tools/ReduxPersistFilter';

// TODO-REDUCER: use SearchUIStateUpdateParams
const INITIAL_STATE = Map({
  mainSearch: Map({
    searchActive: false,
    hasFocus: false,
    autocompleteFocusIndex: -1,
    searchPreventBlur: false,
  }),
  addVarietySearch: Map({
    searchActive: false,
    hasFocus: false,
    autocompleteFocusIndex: -1,
    searchPreventBlur: false,
  }),
  [AppActions.SCROLLING_ENABLED]: true,
  [AppActions.BODY_SCROLLING_ENABLED]: true,
  [Constants.NowPlaying.NowPlayingKeys.IS_NEXT_TRACK_LOADING]: false,
  [Constants.Screen.ScreenKeys.IS_SCREEN_SMALL]: false,
  [Constants.Screen.ScreenKeys.CURRENT_SCREEN_WIDTH]: 0,
  [Constants.Screen.ScreenKeys.CURRENT_SCREEN_HEIGHT]: 0,
  [Constants.Station.StationConstants.IS_STATION_LIST_HOVERED]: false,
  [AppBgKeys.APP_BG]: Map({
    [AppBgKeys.BG_THEME]: BgThemes.DEFAULT,
    [AppBgKeys.NAV_THEME]: 'light',
  }),
  [AppBgKeys.DOMINANT_COLOR]: null,
});
export type UiState = typeof INITIAL_STATE;

// UI REDUCER
export default createReducer(INITIAL_STATE, {
  [AppActions.APPLICATION_BACKGROUND_UPDATE]: applicationBackgroundUpdate,
  [AppActions.UPDATE_DOMINANT_COLOR]: dominantColorUpdate,
  [Constants.Search.SearchActions.SEARCH_UI_UPDATE]: searchUiUpdate,
  [AppActions.ENABLE_SCROLLING]: enableScrolling,
  [AppActions.DISABLE_SCROLLING]: disableScrolling,
  [AppActions.ENABLE_BODY_SCROLLING]: enableBodyScrolling,
  [AppActions.DISABLE_BODY_SCROLLING]: disableBodyScrolling,
  [Constants.UseCases.ActionTypes.SKIP_TRACK_FORWARD]: trackLoading,
  [Constants.Api.ApiActions.EVENT_ENDED_ATTEMPT]: trackLoading,
  [Constants.Playback.ActionTypes.ADD_TRACK_TO_STATION_HISTORY]: trackNotLoading,
  [Constants.Integrations.ActionTypes.TRACK_STARTED_FROM_SOURCE]: trackNotLoading,
  [Constants.Ads.Rewards.ActionTypes.SET_OPEN_COACHMARK]: trackNotLoading,
  [Constants.Overlay.OVERLAY_ACTIONS.OPEN_OVERLAY]: trackNotLoading,
  [Constants.Screen.ScreenActions.SET_SMALL_SCREEN_UI]: setSmallScreenUi,
  [Constants.Screen.ScreenActions.SET_CURRENT_SCREEN_DIMENSIONS]: setCurrentScreenDimensions,
  [ViewActions.HOVERED_OVER_STATION_LIST]: hoveredOverStationList,
  [ViewActions.HOVERED_OUT_STATION_LIST]: hoveredOutStationList,
  [Constants.UseCases.ActionTypes.SKIP_TRACK_FORWARD_CANCEL]: trackNotLoading,
  [Constants.Api.ApiActions.LOGOUT_SUCCESS]: clearUIData,
  [Constants.Playback.APSActions.PLAY_AD_ITEM]: trackNotLoading,
});

// @ts-expect-error
export const UIFilter = createFilter({
  reducer: 'ui',
  keys: [AppBgKeys.DOMINANT_COLOR, AppBgKeys.APP_BG],
  defaults: INITIAL_STATE,
});

// CASE FUNCTIONS

function applicationBackgroundUpdate(
  state: UiState,
  { payload }: ApplicationBackgroundUpdateAction,
): UiState {
  return state.set(AppBgKeys.APP_BG, fromJS(payload));
}

function dominantColorUpdate(state: UiState, { payload }: UpdateDominantColorAction): UiState {
  return state.set(AppBgKeys.DOMINANT_COLOR, payload.color);
}

function searchUiUpdate(state: UiState, { payload }: SearchUIStateUpdateAction): UiState {
  return state.mergeIn([payload.searchType], payload);
}

function enableScrolling(state: UiState): UiState {
  return state.set(AppActions.SCROLLING_ENABLED, true);
}

function disableScrolling(state: UiState): UiState {
  return state.set(AppActions.SCROLLING_ENABLED, false);
}

function enableBodyScrolling(state: UiState): UiState {
  return state.set(AppActions.BODY_SCROLLING_ENABLED, true);
}

function disableBodyScrolling(state: UiState): UiState {
  return state.set(AppActions.BODY_SCROLLING_ENABLED, false);
}

function trackLoading(state: UiState): UiState {
  return state.set(Constants.NowPlaying.NowPlayingKeys.IS_NEXT_TRACK_LOADING, true);
}

function trackNotLoading(state: UiState): UiState {
  return state.set(Constants.NowPlaying.NowPlayingKeys.IS_NEXT_TRACK_LOADING, false);
}

function setSmallScreenUi(state: UiState, { payload }: SetSmallScreenUiAction): UiState {
  return state.set(Constants.Screen.ScreenKeys.IS_SCREEN_SMALL, payload.isScreenSmall);
}

function setCurrentScreenDimensions(
  state: UiState,
  { payload }: SetCurrentScreenDimensionsAction,
): UiState {
  return state.merge({
    [Constants.Screen.ScreenKeys.CURRENT_SCREEN_WIDTH]: payload.screenWidth,
    [Constants.Screen.ScreenKeys.CURRENT_SCREEN_HEIGHT]: payload.screenHeight,
  });
}

function hoveredOverStationList(state: UiState): UiState {
  return state.set(Constants.Station.StationConstants.IS_STATION_LIST_HOVERED, true);
}

function hoveredOutStationList(state: UiState): UiState {
  return state.set(Constants.Station.StationConstants.IS_STATION_LIST_HOVERED, false);
}

function clearUIData(): UiState {
  return INITIAL_STATE;
}
